// Goal types for the goal-based planning feature

export type GoalType = 
  | 'emergency_fund' 
  | 'debt_payoff' 
  | 'down_payment' 
  | 'vacation' 
  | 'education' 
  | 'retirement_boost' 
  | 'custom'

export type GoalStatus = 'active' | 'completed' | 'paused' | 'abandoned'

export interface Contribution {
  id: string
  amount: number
  date: string  // ISO date string
  note: string
}

export interface Goal {
  id: string
  name: string
  type: GoalType
  targetAmount: number
  currentAmount: number
  deadline: string | null        // ISO date or null for no deadline
  createdAt: string
  completedAt: string | null
  status: GoalStatus
  priority: number               // 1 = highest
  color: string                  // For visual distinction
  linkedAccountId: string | null // Optional link to portfolio investment
  linkedAccountType: 'investment' | 'debt' | null // Type of linked account
  notes: string
  contributions: Contribution[]  // History of manual contributions
}

export interface GoalsState {
  goals: Goal[]
  totalSaved: number
  activeGoalsCount: number
  completedGoalsCount: number
}

// Goal type metadata for UI
export interface GoalTypeInfo {
  type: GoalType
  label: string
  icon: string  // Lucide icon name
  color: string // Tailwind color class
  description: string
}

export const GOAL_TYPE_INFO: Record<GoalType, GoalTypeInfo> = {
  emergency_fund: {
    type: 'emergency_fund',
    label: 'Emergency Fund',
    icon: 'ShieldAlert',
    color: 'text-red-500',
    description: 'Build a safety net for unexpected expenses'
  },
  debt_payoff: {
    type: 'debt_payoff',
    label: 'Debt Payoff',
    icon: 'CreditCard',
    color: 'text-orange-500',
    description: 'Pay off existing debts faster'
  },
  down_payment: {
    type: 'down_payment',
    label: 'Down Payment',
    icon: 'Home',
    color: 'text-blue-500',
    description: 'Save for a house or property purchase'
  },
  vacation: {
    type: 'vacation',
    label: 'Vacation',
    icon: 'Plane',
    color: 'text-cyan-500',
    description: 'Fund your dream trip or getaway'
  },
  education: {
    type: 'education',
    label: 'Education',
    icon: 'GraduationCap',
    color: 'text-purple-500',
    description: 'Invest in learning and education'
  },
  retirement_boost: {
    type: 'retirement_boost',
    label: 'Retirement Boost',
    icon: 'Sunset',
    color: 'text-amber-500',
    description: 'Extra savings for retirement'
  },
  custom: {
    type: 'custom',
    label: 'Custom Goal',
    icon: 'Target',
    color: 'text-green-500',
    description: 'Define your own financial goal'
  }
}

// Default colors for goals
export const GOAL_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#8b5cf6', // violet-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
]
